import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, ChevronDown } from "lucide-react";
import { FaPlayCircle } from "react-icons/fa";
import landingImg from "../assets/landing_page_image_1.png";
import landingImg2 from "../assets/landing_page_image_2.jpg";
import landingImg2Mobile from "../assets/video_mobile_image.jpg";
import FreeTrialButton from "../components/landing/FreeTrialButton";
import SharedCTASection from "../components/landing/SharedCTASection";
import hiwGrid1 from "../assets/landing_hiw_grid_1.png";
import hiwGrid2 from "../assets/landing_hiw_grid_2.png";
import hiwGrid3 from "../assets/landing_hiw_grid_3.png";
import hiwGrid4 from "../assets/landing_hiw_grid_4.png";
import hiwCafes from "../assets/hiw_cafes.png";
import hiwRestaurants from "../assets/hiw_restaurants.png";
import hiwRetail from "../assets/hiw_retail.png";
import hiwSalons from "../assets/hiw_salons.png";
import hiwLine from "../assets/hiw_line.png";
import imgChinedu  from "../assets/chinedu-okafor.png";
import imgAisha    from "../assets/aisha-bello.png";
import imgRohit    from "../assets/rohit-sangi.png";
import imgSandeep  from "../assets/sandeep-rao.png";
import imgPooja    from "../assets/pooja-shah.png";
import imgNeha     from "../assets/neha-verma.png";
import imgSneha    from "../assets/sneha-iyer.png";
import imgVikram   from "../assets/vikram-singh.png";
import imgMichael  from "../assets/michael-carter.png";
import imgEthan    from "../assets/ethan-walker.png";
import imgPutri    from "../assets/putri-maheswari.png";
import imgDewi     from "../assets/dewi-lestari.png";
import imgKwame    from "../assets/kwame-mensah.png";
import disconnectedMobile from "../assets/disconnected_mobile.png";

const HERO_VIDEO_URL = process.env.REACT_APP_DEMO_VIDEO_PROMO_VIDEO || null;

const dataChips = [
  "order & pay at table",
  "guest ordering",
  "click & collect",
  "rating and reviews",
];

/* ─────────────────────────────────────────
   HERO
───────────────────────────────────────── */
function Hero() {
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const videoRef = useRef(null);

  // Warm the browser cache so play starts faster on click
  useEffect(() => {
    if (!HERO_VIDEO_URL) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "video";
    link.href = HERO_VIDEO_URL;
    document.head.appendChild(link);

    const warm = document.createElement("video");
    warm.preload = "auto";
    warm.muted = true;
    warm.playsInline = true;
    warm.src = HERO_VIDEO_URL;
    warm.load();

    return () => {
      link.remove();
      warm.removeAttribute("src");
      warm.load();
    };
  }, []);

  const handlePlayClick = () => {
    setBuffering(true);
    setPlaying(true);
  };

  const startPlayback = (el) => {
    videoRef.current = el;
    if (!el) return;

    const markReady = () => setBuffering(false);
    el.addEventListener("playing", markReady, { once: true });
    el.addEventListener("canplay", markReady, { once: true });

    const attempt = el.play();
    if (attempt && attempt.catch) {
      attempt.catch(() => {
        el.muted = true;
        el.play().catch(() => {});
      });
    }
  };

  const renderVideo = () => (
    HERO_VIDEO_URL ? (
      <>
        {buffering && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 pointer-events-none">
            <div
              className="rounded-full border-2 border-white/25 border-t-white animate-spin"
              style={{ width: 40, height: 40 }}
            />
          </div>
        )}
        <video
          ref={startPlayback}
          className="w-full h-full object-contain"
          src={HERO_VIDEO_URL}
          preload="auto"
          autoPlay
          playsInline
        />
      </>
    ) : (
      <p className="text-white/60 text-sm lowercase">video coming soon</p>
    )
  );

  return (
    <section
      id="hero"
      className="relative w-full text-white font-lexend overflow-hidden"
      style={{ backgroundColor: "#020013" }}
    >
      {/* Gradient bg — covers nav area only (373px from top), matching Figma */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none h-[220px] md:h-[373px]"
        style={{
          backgroundImage: `url(${landingImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          zIndex: 0,
        }}
      />

      {/* Top: headline + description + buttons */}
      <div className="relative z-10 flex flex-col md:flex-row gap-6 md:gap-16 items-start justify-between w-full px-4 md:px-16 lg:px-20 pt-36 pb-10 md:pt-44 md:pb-12">

        {/* Left */}
        <div className="w-full md:w-1/2">
          <h1 className="text-[2.2rem] lg:text-[56px] font-bold leading-[120%] lg:leading-none tracking-[-0.04em] lowercase">
            <span style={{ color: "#721AF0" }}>one platform</span>{" "}
            <span style={{
              background: "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.7) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              to serve, sell and grow your business.
            </span>
          </h1>
          <p className="mt-3 md:mt-4 text-white text-[20px] md:text-[28px] font-normal leading-[100%] md:leading-none tracking-[-0.04em] lowercase">
            ai-powered experiences customers actually enjoy.
          </p>
        </div>

        {/* Right */}
        <div className="w-full md:w-1/2 flex flex-col gap-8 md:gap-6 md:pt-3">
          <p className="text-[14px] md:text-[18px] font-normal leading-[160%] tracking-[0.0025em] lowercase" style={{ color: "#CECECE" }}>
            enhance disconnected tools with one platform that helps customers discover you, chat, order instantly, pay seamlessly and keep coming back.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.href = "/business/contact"}
              className="inline-flex items-center justify-center flex-1 md:flex-none md:w-[165px] h-[50px] rounded-full text-white text-sm font-medium lowercase cursor-pointer transition-opacity hover:opacity-80 whitespace-nowrap"
              style={{ border: "1px solid rgba(255,255,255,0.35)" }}
            >
              book demo
            </button>
            <FreeTrialButton onClick={() => window.location.href = "/business/get-started"} className="flex-1 md:flex-none" />
          </div>
        </div>
      </div>

      {/* Bottom: video thumbnail with data chips */}
      <div className={`relative z-10 w-full px-4 sm:px-4 md:px-16 lg:px-20 md:pt-14 ${playing ? "md:pb-14" : "pb-0"}`}>
        <div className="relative w-full h-[400px] md:h-auto md:aspect-video overflow-hidden rounded-[34px]">

          {playing ? (
            <div className="relative w-full h-full bg-black">
              {renderVideo()}
              <button
                onClick={() => setPlaying(false)}
                className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-all z-10"
                aria-label="close video"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            /* Thumbnail state */
            <div className="relative w-full h-full cursor-pointer" onClick={handlePlayClick}>
              {/* Desktop thumbnail */}
              <div className="hidden md:block relative w-full h-full">
                <img
                  src={landingImg2}
                  alt="beɘmz platform demo"
                  className="w-full h-full object-cover"
                />

                <img
                  src={require("../assets/play_button.png")}
                  alt="play"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none"
                  style={{ width: "120px", height: "120px" }}
                />
              </div>
              {/* Mobile thumbnail */}
              <div className="block md:hidden relative overflow-hidden h-[400px]">
                <img
                  src={landingImg2Mobile}
                  alt="beɘmz platform demo"
                  className="block w-full h-full object-cover"
                />

                <img
                  src={require("../assets/play_button.png")}
                  alt="play"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none"
                  style={{ width: "90px", height: "90px" }}
                />
              </div>

              {/* Left-side data chips — desktop only */}
              <div
                className="hidden md:flex absolute flex-col z-10"
                style={{ top: "16px", left: "14px", padding: "20px", gap: "28px", borderRadius: "24px", backgroundColor: "rgba(0,0,0,0.39)", backdropFilter: "blur(6.7px)", WebkitBackdropFilter: "blur(6.7px)" }}
              >
                {dataChips.map((chip) => (
                  <div key={chip} className="flex items-center lowercase"
                    style={{ height: "64px", paddingLeft: "20px", paddingRight: "24px", gap: "14px", borderRadius: "32px", background: "linear-gradient(135deg, rgba(77,77,77,0.85), rgba(12,12,12,0.27))", whiteSpace: "nowrap" }}
                  >
                    <span style={{ width: "14px", height: "14px", borderRadius: "50%", backgroundColor: "#FFFFFF", flexShrink: 0 }} />
                    <span style={{ color: "rgba(255,255,255,0.82)", fontSize: "18px", fontFamily: "Inter, sans-serif", fontWeight: 500 }}>{chip}</span>
                  </div>
                ))}
              </div>

              {/* Left-side data chips — mobile only */}
              <div
                className="flex md:hidden absolute flex-col z-10"
                style={{ top: "16px", left: "6px", padding: "10px", gap: "8px", borderRadius: "16px", backgroundColor: "rgba(0,0,0,0.39)", backdropFilter: "blur(6.7px)", WebkitBackdropFilter: "blur(6.7px)" }}
              >
                {dataChips.map((chip) => (
                  <div key={chip} className="flex items-center lowercase"
                    style={{ height: "36px", paddingLeft: "10px", paddingRight: "14px", gap: "8px", borderRadius: "32px", background: "linear-gradient(135deg, rgba(77,77,77,0.85), rgba(12,12,12,0.27))", whiteSpace: "nowrap" }}
                  >
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#FFFFFF", flexShrink: 0 }} />
                    <span style={{ color: "rgba(255,255,255,0.82)", fontSize: "12px", fontFamily: "Inter, sans-serif", fontWeight: 500 }}>{chip}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   HOW IT WORKS
───────────────────────────────────────── */
const howItWorksSteps = [
  { num: "1", img: hiwGrid1, title: "create your business.", desc: "create your business profile on beɘmz." },
  { num: "2", img: hiwGrid2, title: "generate your qr ordering", desc: "log in to your profile to access your unique qr code." },
  { num: "3", img: hiwGrid3, title: "print or display your qr", desc: "print and place your qr code anywhere in your store or use our templates online." },
  { num: "4", img: hiwGrid4, title: "customers scan & connect", desc: "customers scan, visit your page and can order, chat or get help instantly." },
];

function HowItWorksSection() {
  return (
    <section
      className="relative z-10 w-full px-6 sm:px-10 md:px-16 lg:px-20 pb-16 mt-6 md:-mt-[30px]"
    >
      {/* Frosted glass card */}
      <div
        className="w-full max-w-7xl mx-auto rounded-[32px] md:rounded-[52px] px-4 py-8 md:px-14 md:py-12"
        style={{
          backgroundColor: "rgba(26,26,26,0.73)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
        }}
      >
        <h2 className="text-center text-white text-2xl font-semibold lowercase mb-10">
          how it works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-10">
          {howItWorksSteps.map((step) => (
            <div key={step.title} className="flex flex-col items-center text-center gap-8 md:gap-[38px]">
              <div className="relative flex items-center justify-center flex-shrink-0 w-full md:w-[216px] md:h-[232px]">
                <img
                  src={step.img}
                  alt={step.title}
                  className="w-full md:w-full md:h-full object-contain"
                />
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rounded-full flex items-center justify-center text-white shadow-lg flex-shrink-0"
                  style={{ backgroundColor: "#721AF0", width: "61px", height: "61px", fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "36px", lineHeight: "44px", letterSpacing: "-0.02em" }}
                >
                  {step.num}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-white font-bold text-base md:text-sm lowercase leading-snug">{step.title}</p>
                <p className="text-white/60 text-sm md:text-xs lowercase leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Industry categories — mobile: 2 rows with line */}
      <div className="md:hidden max-w-7xl mx-auto w-full mt-16 flex flex-col gap-10 px-4">
        {[[{ img: hiwCafes, label: "cafés" }, { img: hiwRestaurants, label: "restaurants" }],
          [{ img: hiwRetail, label: "retail" }, { img: hiwSalons, label: "salons" }]].map((row, ri) => (
          <div key={ri} className="flex items-center w-full">
            {row.map(({ img, label }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <img src={hiwLine} alt="" className="h-16 w-auto object-contain flex-shrink-0 mx-4" />}
                <div className="flex flex-col items-center gap-3 flex-1">
                  <img src={img} alt={label} className="h-16 w-auto object-contain" />
                  <p className="lowercase text-white/80 text-sm" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}>{label}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        ))}
      </div>

      {/* Industry categories — desktop: single row with lines */}
      <div className="hidden md:flex max-w-7xl mx-auto w-full mt-20 items-center justify-between">
        {[
          { img: hiwCafes, label: "cafés" },
          { img: hiwRestaurants, label: "restaurants" },
          { img: hiwRetail, label: "retail" },
          { img: hiwSalons, label: "salons" },
        ].map(({ img, label }, i) => (
          <React.Fragment key={label}>
            {i > 0 && <img src={hiwLine} alt="" className="h-16 object-contain flex-shrink-0" />}
            <div className="flex flex-col items-center gap-4 flex-1">
              <img src={img} alt={label} className="h-20 w-auto object-contain" />
              <p className="lowercase text-white/80 text-lg" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}>{label}</p>
            </div>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const beforeItems = [
  "one-time visitors",
  "no customer data or customer history",
  "slow manual order-taking",
  "long queues and longer wait times",
  "no follow-up after purchase",
  "no loyalty or rewards program",
  "generic customer experience",
  "no personalized offers",
  "customers choose competitors next time",
  "limited visibility into sales and operations",
];
const afterItems = [
  "reduce order-taking time with ai & qr ordering",
  "build customer profiles automatically (crm)",
  "launch targeted marketing campaigns",
  "reward customers with loyalty & offers",
  "integrate seamlessly with your existing pos",
  "gain operational analytics on sales, wait times & performance",
  "personalize every customer interaction",
  "re-engage customers with smart campaigns",
  "smart re-engagement campaigns",
  "turn first-time visitors into loyal customers",
];

const painPoints = [
  { title: "too many subscriptions", body: "paying for 10+ apps that don't communicate with each other.", side: "left" },
  { title: "too many disconnected tools", body: "ordering, payments, marketing, loyalty, and operations all live in different platforms that don't work together.", side: "right" },
  { title: "disconnected customer data", body: "manual exports and imports just to send a simple email blast.", side: "left" },
  { title: "growing costs", body: "more subscriptions, more commissions, more hardware, and more hidden fees reduce your margins as your business grows.", side: "right" },
];

const features = [
  { title: "ai ordering", body: "natural language processing allows customers to order via chat with intelligent upselling.", img: "ai_ordering.png", icon: "ai_ordering_icon.png" },
  { title: "business profile", body: "a high-fidelity digital storefront that converts social traffic into direct sales.", img: "business_profile.png", icon: "business_profile_icon.png" },
  { title: "qr ordering", body: "contactless, app-less ordering that integrates directly into your kitchen display.", img: "qr_ordering.png", icon: "qr_ordering_icon.png" },
  { title: "payments", body: "unified payment processing with lower rates and instant settlements.", img: "qr_payments.png", icon: "payments_icon.png" },
  { title: "engagement", body: "automated loyalty programs and re-engagement campaigns that actually work.", img: "engagement_work.png", icon: "engagement_work_icon.png" },
  { title: "analytics", body: "deep insights into customer behavior across every channel in real-time.", img: "analytics.png", icon: "analytics_icon.png" },
];

const industries = [
  { label: "restaurant", img: "restaurant.png" },
  { label: "retail", img: "retail.png" },
  { label: "café", img: "cafe_tile.png" },
  { label: "salon", img: "salon.png" },
  { label: "bakery", img: "bakery.png" },
  { label: "gym", img: "gym.png" },
  { label: "hotel", img: "hotel.png" },
  { label: "events", img: "events.png" },
  { label: "cloud kitchen", img: "cloud_kitchen.png" },
];

const caseStudies = [
  { headline: "instore convenience, made simple." },
  { headline: "loyalty that keeps customers coming back." },
  { headline: "smarter ordering, happier customers." },
];

const testimonials = [
  { name: "Michael Carter", role: "restaurant manager", avatar: imgMichael, badgeColor: "#C8E6C9", quote: "we were handling most things manually before. now it's all more streamlined and saves us time daily." },
  { name: "Ethan Walker", role: "cafe owner", avatar: imgEthan, badgeColor: "#B3E5FC", quote: "planning and managing everything used to take a lot of effort. now it feels more organised and manageable." },
  { name: "Chinedu Okafor", role: "cafe owner", avatar: imgChinedu, badgeColor: "#F5CBA7", quote: "managing everything earlier was a bit stressful at times. now it feels more in control and less chaotic during peak hours." },
  { name: "Aisha Bello", role: "salon owner", avatar: imgAisha, badgeColor: "#F4C2C2", quote: "didn't expect much at first, but it's been helpful in daily work. things just run a bit smoother now." },
  { name: "Rohit Sangi", role: "salon owner", avatar: imgRohit, badgeColor: "#F4C2C2", quote: "earlier things used to get a bit unorganised during busy hours. now it feels more structured and easier to handle overall." },
  { name: "Sandeep Rao", role: "restaurant owner", avatar: imgSandeep, badgeColor: "#CDB7F6", quote: "managing everything earlier was a bit stressful at times. now it feels more in control and less chaotic during peak hours." },
  { name: "Pooja Shah", role: "boutique owner", avatar: imgPooja, badgeColor: "#F5CBA7", quote: "it's simple to use and doesn't take much time to figure out. i can manage things better now without depending on others." },
  { name: "Neha Verma", role: "cafe owner", avatar: imgNeha, badgeColor: "#B3E5FC", quote: "didn't expect much at first, but it's been helpful in day-to-day work. it just makes things a bit smoother without extra effort." },
  { name: "Sneha Iyer", role: "home bakery", avatar: imgSneha, badgeColor: "#CDB7F6", quote: "i like that it's not complicated. just helps keep things in order without adding extra work." },
  { name: "Vikram Singh", role: "gym owner", avatar: imgVikram, badgeColor: "#C8E6C9", quote: "setup was quick and easy. after that, managing day-to-day tasks became much simpler." },
  { name: "Putri Maheswari", role: "boutique owner", avatar: imgPutri, badgeColor: "#F5CBA7", quote: "it's simple to use and doesn't take much time to figure out. i can manage things better now without depending on others." },
  { name: "Dewi Lestari", role: "home bakery", avatar: imgDewi, badgeColor: "#CDB7F6", quote: "i like that it's not complicated. just helps keep things in order without adding extra work." },
  { name: "Kwame Mensah", role: "cloud kitchen owner", avatar: imgKwame, badgeColor: "#F4C2C2", quote: "earlier we didn't have much clarity on daily operations. now it's easier to track and stay on top of things." },
];

const stats = [
  { value: "200+", label: "businesses trust us" },
  { value: "5000+", label: "customers served" },
  { value: "98%", label: "retention rate" },
  { value: "24/7", label: "support available" },
];

const faqs = [
  { q: "what is beɘmz?", a: "beɘmz is a real time chat app and marketplace which enables businesses to engage with their customers, enhancing their guest experience by processing orders and enquiries faster, managing payments & operations, marketing and growth all in one place." },
  { q: "how does the 0% commission model work?", a: "Unlike marketplace apps, beɘmz doesn't take a commission on every order—you keep more of your revenue while paying only your chosen subscription plan." },
  { q: "is beɘmz only for restaurants?", a: "No. beɘmz is built for restaurants, cafés, salons, retail stores, clinics, service providers, and many other local businesses." },
  { q: "how does ai ordering work?", a: "Customers can chat naturally to discover products, place orders, make bookings, and get instant recommendations powered by AI." },
  { q: "can i integrate beɘmz with my current software?", a: "Yes. beɘmz supports integrations with compatible POS systems and business tools to fit into your existing workflow." },
  { q: "what kind of analytics will i get?", a: "Track sales, customer activity, orders, engagement, business performance, and other insights through a real-time analytics dashboard." },
  { q: "how secure are the payments?", a: "Payments are processed through trusted payment providers using secure, industry-standard encryption to protect every transaction." },
  { q: "how long does it take to set up?", a: "Most businesses can get started in just a few minutes by creating an account, setting up their profile, and choosing a subscription plan." },
  { q: "do you offer customer support?", a: "Yes. Our support team is available to help with onboarding, setup, and any questions as your business grows." },
  { q: "can i switch plans later?", a: "Absolutely. You can upgrade or downgrade your subscription anytime from your Business Portal based on your business needs." },
];

/* ─────────────────────────────────────────
   SECTION 1b: WHY BUSINESSES CHOOSE
───────────────────────────────────────── */
const whyCards = [
  {
    title: "faster ordering",
    desc: "customers scan and pay directly at the table. no more waiting for staff or menus.",
    icon: require("../assets/fast_ordering.png"),
  },
  {
    title: "click & collect",
    desc: "online pre-orders for busy cafes and retail counters. zero wait time for pickups.",
    icon: require("../assets/click_connect.png"),
  },
  {
    title: "ai engagement & personalization",
    desc: "leverage ai to suggest perfect pairings and increase your average order value automatically with personalized offers.",
    icon: require("../assets/ai_engagement.png"),
  },
];

const whyStats = [
  { label: "lower order time",   sub: "qr + ai ordering",       icon: require("../assets/lower_order.png") },
  { label: "table turnover",     sub: "pay-at-table flow",       icon: require("../assets/table_turnover.png") },
  { label: "zero order errors",  sub: "self-service accuracy",   icon: require("../assets/zero_order_errors.png") },
  { label: "repeat visits",      sub: "loyalty & crm",           icon: require("../assets/repeat_visits.png") },
];

const whyChips = [
  { label: "qr ordering",    icon: require("../assets/qr_ordering_chip.png") },
  { label: "order at table", icon: require("../assets/order_at_table.png") },
  { label: "pay at table",   icon: require("../assets/pay_at_table.png") },
  { label: "click & collect",icon: require("../assets/click_collect_chip.png") },
  { label: "guest ordering", icon: require("../assets/guest_ordering.png") },
];

function WhyBusinessesChooseSection() {
  return (
    <section
      className="relative z-10 w-full px-5 sm:px-8 md:px-20 py-14 md:py-20"
      style={{
        background: `
          radial-gradient(35.1% 39.3% at 100% 72.18%, rgba(112,0,255,0.1) 0%, rgba(112,0,255,0) 50%),
          radial-gradient(90% 100.77% at 0.97% 45.08%, rgba(112,0,255,0.09) 0%, rgba(112,0,255,0) 50%)
        `,
      }}
    >
      <div className="max-w-7xl mx-auto flex flex-col gap-10 md:gap-12">

        {/* Heading */}
        <div className="flex flex-col items-center gap-0">
          <h2
            className="lowercase text-center relative"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(32px, 7.5vw, 60px)", lineHeight: "120%", letterSpacing: "0px", color: "#F1F5F9" }}
          >
            why businesses<br className="md:hidden" /> choose <span style={{ color: "#721AF0" }}>digital guest<br className="md:hidden" /><span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>experiences<img src={require("../assets/dollar_growth.png")} alt="" className="w-10 h-10 md:w-[71px] md:h-[71px]" style={{ transform: "rotate(5.8deg)", flexShrink: 0, position: "relative", top: "4px" }} /></span></span>
          </h2>
        </div>

        {/* Image + Cards */}
        <div className="flex flex-col md:flex-row gap-8" style={{ minHeight: "400px" }}>
          {/* Mobile image — no wrapper */}
          <img
            src={require("../assets/smart_qr_ordering_mobile.png")}
            alt="smart qr ordering"
            className="w-full object-cover rounded-[24px] md:hidden"
          />
          {/* Desktop image — no wrapper */}
          <img
            src={require("../assets/smart_qr_ordering.png")}
            alt="smart qr ordering"
            className="hidden md:block md:w-[45.7%] flex-shrink-0 object-cover rounded-[24px]"
            style={{ height: "461px" }}
          />

          {/* Right: 3 feature cards */}
          <div className="flex flex-col flex-1" style={{ gap: "16px" }}>
            {whyCards.map((card, i) => (
              <div
                key={i}
                className="flex flex-col md:flex-row md:items-center"
                style={{
                  flex: 1,
                  backgroundColor: "#0D0D1A",
                  borderRadius: "16px",
                  border: "1px solid rgba(255,255,255,0.07)",
                  padding: "20px",
                  gap: "12px",
                }}
              >
                <img
                  src={card.icon}
                  alt={card.title}
                  style={{ flexShrink: 0, width: "55px", height: "55px", objectFit: "contain" }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "20px", lineHeight: "28px", letterSpacing: "0px", color: "#FFFFFF", marginBottom: "4px" }}>{card.title}</p>
                  <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "16px", lineHeight: "22.75px", letterSpacing: "0px", color: "#A7A7A7" }}>{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:pl-16"
          style={{ borderTop: "1px solid #222226", paddingTop: "32px" }}
        >
          {whyStats.map((s, i) => (
            <div key={i} className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-4 text-center md:text-left">
              <img src={s.icon} alt={s.label} style={{ flexShrink: 0, width: "48px", height: "48px", objectFit: "contain" }} />
              <div>
                <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "17px", color: "#FFFFFF", lineHeight: "1.3" }}>{s.label}</p>
                <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "15px", color: "rgba(255,255,255,0.5)", marginTop: "3px" }}>{s.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Chips */}
        <div className="flex flex-wrap gap-3 justify-center">
          {whyChips.map((chip) => (
            <span
              key={chip.label}
              className="lowercase"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "18.24px",
                letterSpacing: "0px",
                color: "#94A3B8",
                backgroundColor: "#131316",
                border: "1.14px solid #222226",
                borderRadius: "9999px",
                padding: "9px 18px",
                whiteSpace: "nowrap",
              }}
            >
              <img src={chip.icon} alt="" style={{ width: "16px", height: "16px", objectFit: "contain" }} />
              {chip.label}
            </span>
          ))}
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   SECTION 2: CUSTOMERS DISAPPEARING
───────────────────────────────────────── */
function CustomersDisappearingSection() {
  return (
    <section className="relative z-10 w-full px-1 sm:px-5 md:px-16 lg:px-20 pt-8 pb-10 md:pt-14 md:pb-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="lowercase text-center mb-4 md:mb-6" style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          fontSize: "clamp(32px, 8vw, 56px)",
          lineHeight: "115%",
          letterSpacing: "-0.04em",
        }}>
          <span style={{ color: "#721AF0" }}>your customers</span>{" "}
          <span style={{
            background: "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.7) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>
            are disappearing. you just<br />don't know why.
          </span>
        </h2>
        <p className="lowercase text-center w-full md:max-w-5xl md:mx-auto mb-8 md:mb-16" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(14px, 2.5vw, 20px)", lineHeight: "160%", letterSpacing: "0px", color: "#FFFFFF" }}>
          most customers visit once and never return. without meaningful engagement, personalized experiences, or follow-up, businesses lose valuable relationships every single day. every lost customer means lost revenue, missed referrals, and slower growth.
        </p>

        {/* Before / After cards */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-10">
          {/* Before */}
          <div className="flex flex-col p-5 gap-5 md:p-[40px] md:gap-8"
            style={{
              backgroundColor: "#101017",
              borderRadius: "32px",
              border: "1px solid rgba(74,68,87,0.2)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
            }}>
            <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "clamp(16px, 4.5vw, 24px)", lineHeight: "28px", letterSpacing: "0px", color: "#CCC3DA" }}>before (current reality)</p>
            <div className="flex flex-col gap-2 md:gap-4">
              {beforeItems.map(item => (
                <div key={item} className="flex items-center gap-3">
                  <img src={require("../assets/cross_icon.png")} alt="" className="flex-shrink-0" style={{ width: "clamp(12px,3.5vw,16px)", height: "clamp(12px,3.5vw,16px)", objectFit: "contain" }} />
                  <span className="text-white lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(13px,3.5vw,20px)", lineHeight: "160%", letterSpacing: "0px" }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* With beɘmz */}
          <div className="relative flex flex-col p-5 gap-5 md:p-[40px] md:gap-8"
            style={{
              backgroundColor: "#101017",
              borderRadius: "32px",
              border: "1px solid #A166F5",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: "0 0 0 1px rgba(161,102,245,0.4), 0 0 40px rgba(112,0,255,0.35), 0 0 80px rgba(112,0,255,0.15)",
            }}>
            <img src={require("../assets/landing_logo.png")} alt="beɘmz" className="absolute object-contain" style={{ width: "32px", height: "32px", top: "16px", right: "16px" }} />
            <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "clamp(16px, 4.5vw, 24px)", lineHeight: "28px", letterSpacing: "0px", color: "#FFFFFF" }}>with beɘmz</p>
            <div className="flex flex-col gap-2 md:gap-4">
              {afterItems.map(item => (
                <div key={item} className="flex items-center gap-3">
                  <img src={require("../assets/right_icon.png")} alt="" className="flex-shrink-0" style={{ width: "clamp(12px,3.5vw,16px)", height: "clamp(12px,3.5vw,16px)", objectFit: "contain" }} />
                  <span className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(13px,3.5vw,20px)", lineHeight: "160%", letterSpacing: "0px", color: "#E7DFF0" }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   SECTION 3: FIVE PLATFORMS
───────────────────────────────────────── */
function FivePlatformsSection() {
  return (
    <section className="relative z-10 w-full px-1 sm:px-5 md:px-16 lg:px-20 py-10 md:py-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="lowercase mb-4 md:mb-5" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(32px, 8vw, 56px)", lineHeight: "100%", letterSpacing: "-0.89px" }}>
          <span style={{ color: "#721AF0" }}>running a business<br />shouldn't</span>{" "}
          <span style={{ color: "#E7DFF0" }}>require five<br />different platforms.</span>
        </h2>
        <p className="lowercase mb-8 md:mb-14" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(14px, 2.5vw, 18px)", lineHeight: "160%", letterSpacing: "0.45px", color: "#FFFFFF" }}>
          businesses today use separate tools for payments, ordering, customer engagement, loyalty, marketing and operations. more software means more complexity, higher costs and disconnected customer experiences.
        </p>
        <img src={disconnectedMobile} alt="disconnected tools" className="block md:hidden w-full mb-8 rounded-[20px]" />
        <img src={require("../assets/disconnected.png")} alt="disconnected tools" className="hidden md:block w-full mb-14 rounded-[24px]" />
        <div className="flex flex-col gap-4 md:gap-5">
          {painPoints.map((p, i) => (
            <div key={i}
              className={`w-full md:w-[52%] flex flex-col ${p.side === "right" ? "md:ml-auto" : "md:mr-auto"}`}
              style={{
                backgroundColor: "#1A1A1A99",
                borderRadius: "24px",
                borderTop: "1px solid rgba(112,0,255,0.1)",
                borderRight: "1px solid rgba(112,0,255,0.1)",
                borderBottom: "1px solid rgba(112,0,255,0.1)",
                borderLeft: "4px solid #7000FF",
                padding: "20px",
                gap: "10px",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
              }}>
              <h3 className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "clamp(16px, 4.5vw, 28px)", lineHeight: "1.2", letterSpacing: "0px", color: "#721AF0" }}>{p.title}</h3>
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(13px, 3.5vw, 20px)", lineHeight: "160%", letterSpacing: "0px", color: "#FFFFFF" }}>{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   SECTION 4: KEY FEATURES
───────────────────────────────────────── */
function KeyFeaturesSection() {
  const [activeFeature, setActiveFeature] = useState(0);
  const featureScrollRef = useRef(null);

  const handleFeatureScroll = () => {
    const el = featureScrollRef.current;
    if (!el || !el.children.length) return;
    const cardW = el.children[0].offsetWidth + 16;
    setActiveFeature(Math.min(features.length - 1, Math.round(el.scrollLeft / cardW)));
  };

  return (
    <section className="relative z-10 w-full px-1 sm:px-5 md:px-16 lg:px-20 py-10 md:py-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="lowercase text-center mb-3 md:mb-4" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(26px, 8vw, 56px)", lineHeight: "100%", letterSpacing: "-0.8px" }}>
          <span className="text-white">everything works better when <br /></span>
          <span style={{ color: "#721AF0" }}>everythingworks together.</span>
        </h2>
        <p className="lowercase text-center mb-6 md:mb-14" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(20px, 7vw, 24px)", lineHeight: "1.5", letterSpacing: "0px", color: "#CCC3DA" }}>
          one platform to manage every touchpoint of your business.
        </p>
        <div className="w-full mb-12 md:mb-14">
          <img src={require("../assets/everything_works_better.png")} alt="everything works better" className="w-full rounded-[24px]" />
        </div>

        {/* Mobile: horizontal scroll */}
        <div
          ref={featureScrollRef}
          onScroll={handleFeatureScroll}
          className="md:hidden overflow-x-auto flex flex-nowrap gap-4 pb-2 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {features.map((f, i) => (
            <div key={i} className="flex flex-col flex-shrink-0 snap-start w-[calc(100vw-56px)]"
              style={{
                backgroundColor: "rgba(26,26,26,0.6)",
                borderRadius: "24px",
                border: "1px solid rgba(112,0,255,0.1)",
                padding: "20px",
                gap: "12px",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
              }}>
              <div className="flex items-center gap-3">
                <img src={require(`../assets/${f.icon}`)} alt={f.title} className="w-6 h-6 object-contain flex-shrink-0" />
                <h3 className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "17px", lineHeight: "24px", color: "#FFFFFF" }}>{f.title}</h3>
              </div>
              <p className="text-white/70 text-sm lowercase leading-relaxed" >{f.body}</p>
              <div className="mt-1 w-full overflow-hidden rounded-xl flex-1">
                <img src={require(`../assets/${f.img}`)} alt={f.title} className="w-full h-full object-cover" />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile dots */}
        <div className="md:hidden flex justify-center gap-2 mt-4">
          {features.map((_, i) => (
            <div key={i} style={{
              width: i === activeFeature ? "24px" : "8px",
              height: "8px",
              borderRadius: "9999px",
              backgroundColor: i === activeFeature ? "#721AF0" : "rgba(255,255,255,0.25)",
              transition: "all 0.25s ease",
            }} />
          ))}
        </div>

        {/* Desktop: grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div key={i} className="flex flex-col"
              style={{
                backgroundColor: "rgba(26,26,26,0.6)",
                borderRadius: "32px",
                border: "1px solid rgba(112,0,255,0.1)",
                padding: "40px",
                gap: "16px",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
              }}>
              <div className="flex items-center gap-3">
                <img src={require(`../assets/${f.icon}`)} alt={f.title} className="w-8 h-8 object-contain flex-shrink-0" />
                <h3 className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "24px", lineHeight: "32px", letterSpacing: "0px", color: "#FFFFFF" }}>{f.title}</h3>
              </div>
              <p className="text-white/70 text-base lowercase leading-relaxed">{f.body}</p>
              <div className="mt-2 w-full overflow-hidden rounded-2xl flex-1">
                <img src={require(`../assets/${f.img}`)} alt={f.title} className="w-full h-full object-cover" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   SECTION 5: PLATFORM OVERVIEW
───────────────────────────────────────── */
function PlatformOverviewSection() {
  return (
    <section className="relative z-10 w-full px-1 sm:px-5 md:px-16 lg:px-20 py-10 md:py-24">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-8 md:gap-16 items-center">
        {/* Left: chat mockup */}
        <div className="flex-shrink-0 w-full md:w-[484px] md:h-[398px]">
          <img src={require("../assets/chat_mockup.png")} alt="chat mockup" className="w-full h-full rounded-[24px] md:rounded-[32px] object-contain" />
        </div>

        {/* Right: text */}
        <div className="flex flex-col w-full gap-6 md:gap-[44px]">
          <h2 className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(24px, 7vw, 56px)", lineHeight: "100%", letterSpacing: "-0.8px" }}>
            <span style={{ color: "#FFFFFF" }}>our platform isn't just another tool. </span>
            <span style={{ color: "#721AF0" }}>it's how<br />your business works.</span>
          </h2>
          <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(15px, 2.5vw, 18px)", lineHeight: "150%", letterSpacing: "0px", color: "#F9F9F9" }}>
            traditional software waits for inputs. beɘmz anticipates them. from optimizing your inventory levels to having natural conversations with your customers, our platform works as an expert in your team.
          </p>
          <div className="flex flex-col gap-6 md:grid md:grid-cols-2 md:gap-6">
            <div className="flex flex-col gap-2">
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "24px", lineHeight: "24px", letterSpacing: "0px", color: "#FFFFFF" }}>24/7 ordering</p>
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(15px, 2.5vw, 18px)", lineHeight: "160%", letterSpacing: "0px", color: "#F9F9F9" }}>never miss a customer query, even when your staff is busy.</p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "24px", lineHeight: "24px", letterSpacing: "0px", color: "#FFFFFF" }}>intelligent upselling</p>
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(15px, 2.5vw, 18px)", lineHeight: "160%", letterSpacing: "0px", color: "#F9F9F9" }}>increase average order value by 18% with smart recommendations.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   SECTION 6: INDUSTRIES
───────────────────────────────────────── */
function IndustriesSection() {
  return (
    <section className="relative z-10 w-full px-1 sm:px-5 md:px-16 lg:px-20 py-10 md:py-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="lowercase text-center mb-6 md:mb-10" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(32px, 8vw, 56px)", lineHeight: "100%", letterSpacing: "-0.8px", color: "#E7DFF0" }}>
          built for any <span style={{ color: "#721AF0" }}>place</span>
        </h2>
        <div className="grid grid-cols-3 md:grid-cols-9 gap-3">
          {industries.map((ind, i) => (
            <img
              key={i}
              src={require(`../assets/${ind.img}`)}
              alt={ind.label}
              className="w-full object-contain"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   SECTION 7: CASE STUDIES
───────────────────────────────────────── */
const caseStudyItems = [
  { img: "restaurant_growth_cs.png", category: "restaurant growth program", name: "turning first-time visitors into loyal customers", cs: require("../assets/cs_6.png") },
  { img: "inde_restaurant.png",      category: "independent restaurant",    name: "bella's kitchen",                                 cs: require("../assets/cs_2.png") },
  { img: "food_truck_cs.png",        category: "food truck",                name: "streetbite",                                      cs: require("../assets/cs_4.png") },
  { img: "cafe_cs.png",              category: "cafe chain",                name: "brew & co.",                                      cs: require("../assets/cs_3.png") },
  { img: "salon_cs.png",             category: "salon",                     name: "glamour studio",                                  cs: require("../assets/cs_5.png") },
  { img: "pharmacy_cs.png",          category: "pharmacy",                  name: "healthplus pharmacy",                             cs: require("../assets/cs_1.png") },
];

function CaseStudiesSection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [desktopPage, setDesktopPage] = useState(0);
  const [modalItem, setModalItem] = useState(null);
  const caseScrollRef = useRef(null);
  const desktopScrollRef = useRef(null);

  useEffect(() => {
    if (modalItem) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [modalItem]);

  const handleCaseScroll = () => {
    const el = caseScrollRef.current;
    if (!el || !el.children.length) return;
    const cardW = el.children[0].offsetWidth + 16;
    setActiveIdx(Math.min(caseStudyItems.length - 1, Math.round(el.scrollLeft / cardW)));
  };

  const scrollDesktopToPage = (page) => {
    const el = desktopScrollRef.current;
    if (!el) return;
    el.scrollLeft = page === 0 ? 0 : el.scrollWidth - el.clientWidth;
    setDesktopPage(page);
  };

  const handleDownloadPdf = () => {
    if (!modalItem) return;
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>case study</title><style>*{margin:0;padding:0;box-sizing:border-box}body{background:#000;display:flex;justify-content:center}img{width:100%;max-width:800px;display:block}@media print{body{background:#fff}}</style></head><body><img src="${modalItem.cs}" onload="window.print()" /></body></html>`);
    win.document.close();
  };

  return (
    <section className="relative z-10 w-full px-1 sm:px-5 md:px-16 lg:px-20 pt-4 pb-12 md:pb-24">
      <div className="max-w-7xl mx-auto flex flex-col gap-6 md:gap-10">
        <h2
          className="lowercase text-center"
          style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(26px, 8vw, 56px)", lineHeight: "100%", letterSpacing: "-0.04em", color: "#721AF0" }}
        >
          case study
        </h2>

        {/* Mobile: horizontal scroll — full-bleed, one card per view */}
        <div
          ref={caseScrollRef}
          onScroll={handleCaseScroll}
          className="md:hidden overflow-x-auto flex flex-nowrap gap-4 pb-2 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-1 sm:-mx-2 px-1 sm:px-4"
        >
          {caseStudyItems.map((item, i) => (
            <div
              key={i}
              className="flex-shrink-0 snap-start"
              onClick={() => setModalItem(item)}
              style={{
                width: "calc(100vw - 48px)",
                borderRadius: "20px",
                border: "1px solid rgba(112,0,255,0.6)",
                backgroundColor: "rgba(26,26,26,0.6)",
                boxShadow: "0px 0px 12px 0px rgba(112,0,255,0.2)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                cursor: "pointer",
              }}
            >
              <img src={require(`../assets/${item.img}`)} alt={item.name} className="w-full object-cover flex-shrink-0" style={{ height: "220px", borderRadius: "10px" }} />
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "15px", lineHeight: "1.3", color: "#FFFFFF" }}>{item.category}</p>
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "13px", color: "#721AF0" }}>{item.name}</p>
            </div>
          ))}
        </div>

        {/* Mobile dots — 1 per card (6 total) */}
        <div className="md:hidden flex justify-center items-center gap-2 -mt-2">
          {caseStudyItems.map((_, i) => (
            <div
              key={i}
              onClick={() => {
                const el = caseScrollRef.current;
                if (!el || !el.children.length) return;
                const cardW = el.children[0].offsetWidth + 16;
                el.scrollTo({ left: i * cardW, behavior: "smooth" });
              }}
              style={{
                width: i === activeIdx ? "24px" : "8px",
                height: "8px",
                borderRadius: "9999px",
                backgroundColor: i === activeIdx ? "#721AF0" : "rgba(255,255,255,0.25)",
                transition: "all 0.25s ease",
                cursor: "pointer",
              }}
            />
          ))}
        </div>

        {/* Desktop: horizontal scroll, 3 cards per page */}
        <div ref={desktopScrollRef} className="hidden md:flex gap-5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* Page 1: cards 0-2 */}
          <div className="flex-shrink-0 w-full grid grid-cols-3 gap-5 snap-start">
            {caseStudyItems.slice(0, 3).map((item, i) => (
              <div key={i} onClick={() => setModalItem(item)} style={{ height: "371px", borderRadius: "32px", border: "1px solid rgba(112,0,255,0.6)", backgroundColor: "rgba(26,26,26,0.6)", boxShadow: "0px 0px 12px 0px rgba(112,0,255,0.2)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", padding: "24px", display: "flex", flexDirection: "column", gap: "13px", cursor: "pointer" }}>
                <img src={require(`../assets/${item.img}`)} alt={item.name} className="w-full object-cover flex-shrink-0" style={{ height: "252px", borderRadius: "6px" }} />
                <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "18px", lineHeight: "1.3", color: "#FFFFFF" }}>{item.category}</p>
                <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "16px", color: "#721AF0" }}>{item.name}</p>
              </div>
            ))}
          </div>
          {/* Page 2: cards 3-5 */}
          <div className="flex-shrink-0 w-full grid grid-cols-3 gap-5 snap-start">
            {caseStudyItems.slice(3).map((item, i) => (
              <div key={i} onClick={() => setModalItem(item)} style={{ height: "371px", borderRadius: "32px", border: "1px solid rgba(112,0,255,0.6)", backgroundColor: "rgba(26,26,26,0.6)", boxShadow: "0px 0px 12px 0px rgba(112,0,255,0.2)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", padding: "24px", display: "flex", flexDirection: "column", gap: "13px", cursor: "pointer" }}>
                <img src={require(`../assets/${item.img}`)} alt={item.name} className="w-full object-cover flex-shrink-0" style={{ height: "252px", borderRadius: "6px" }} />
                <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "18px", lineHeight: "1.3", color: "#FFFFFF" }}>{item.category}</p>
                <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "16px", color: "#721AF0" }}>{item.name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop pagination dots — 2 page dots */}
        <div className="hidden md:flex justify-center items-center gap-2 mt-2">
          {[0, 1].map((pageIdx) => (
            <div
              key={pageIdx}
              onClick={() => scrollDesktopToPage(pageIdx)}
              style={{
                width: desktopPage === pageIdx ? "24px" : "8px",
                height: "8px",
                borderRadius: "9999px",
                backgroundColor: desktopPage === pageIdx ? "#721AF0" : "rgba(255,255,255,0.25)",
                transition: "all 0.25s ease",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      </div>

      {/* Case Study Modal — rendered via portal to escape parent stacking context */}
      {modalItem && ReactDOM.createPortal(
        <div
          onClick={() => setModalItem(null)}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          style={{ position: "fixed", inset: 0, zIndex: 9999, backgroundColor: "rgba(2,0,19,0.96)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 16px", overflow: "hidden" }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "100%", maxWidth: "480px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={handleDownloadPdf}
                style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#721AF0", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 14px", fontSize: "13px", fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", whiteSpace: "nowrap" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                download pdf
              </button>
              <button
                onClick={() => setModalItem(null)}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", backgroundColor: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", cursor: "pointer", fontSize: "18px", lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            <img src={modalItem.cs} alt={modalItem.name} style={{ width: "auto", maxWidth: "100%", maxHeight: "calc(100vh - 120px)", borderRadius: "16px", display: "block", margin: "0 auto" }} />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}

/* ─────────────────────────────────────────
   SECTION 8: TESTIMONIALS + STATS
───────────────────────────────────────── */
const CARD_WIDTH_DESKTOP = 424; // 400px card + 24px gap
const CARD_WIDTH_MOBILE = 316; // 300px card + 16px gap
const CARDS_PER_PAGE_DESKTOP = 4;
const DESKTOP_DOT_COUNT = Math.ceil(testimonials.length / CARDS_PER_PAGE_DESKTOP);

function StatsTestimonialsSection() {
  const trackRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const [activeDot, setActiveDot] = useState(0);

  const handleTrackScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const cardW = window.innerWidth < 768 ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
    const idx = Math.min(testimonials.length - 1, Math.max(0, Math.round(el.scrollLeft / cardW)));
    setActiveDot(idx);
  };

  const scrollToCard = (idx) => {
    const el = trackRef.current;
    if (!el) return;
    const cardW = window.innerWidth < 768 ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
    el.scrollTo({ left: idx * cardW, behavior: "smooth" });
  };

  const scrollToPage = (pageIdx) => {
    const el = trackRef.current;
    if (!el) return;
    if (pageIdx === DESKTOP_DOT_COUNT - 1) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    } else {
      el.scrollTo({ left: pageIdx * CARDS_PER_PAGE_DESKTOP * CARD_WIDTH_DESKTOP, behavior: "smooth" });
    }
  };

  // Last dot activates whenever we're within the final page's range (can't scroll far enough to make last card leftmost)
  const activeDesktopDot = activeDot >= testimonials.length - CARDS_PER_PAGE_DESKTOP
    ? DESKTOP_DOT_COUNT - 1
    : Math.floor(activeDot / CARDS_PER_PAGE_DESKTOP);

  const onMouseDown = (e) => {
    drag.current = { active: true, startX: e.pageX, scrollLeft: trackRef.current.scrollLeft };
    trackRef.current.style.cursor = "grabbing";
  };
  const onMouseMove = (e) => {
    if (!drag.current.active) return;
    trackRef.current.scrollLeft = drag.current.scrollLeft - (e.pageX - drag.current.startX);
  };
  const onMouseUp = () => {
    drag.current.active = false;
    if (trackRef.current) trackRef.current.style.cursor = "grab";
  };

  return (
    <section className="relative z-10 w-full py-10 md:py-24">
      {/* Testimonials */}
      <h2 className="lowercase text-center mb-6 md:mb-10 px-1 sm:px-5 md:px-16 lg:px-20" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(26px, 8vw, 56px)", lineHeight: "100%", letterSpacing: "-0.8px", color: "#721AF0" }}>
        testimonials
      </h2>

      {/* Carousel */}
      <div className="relative w-full" style={{ WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)", maskImage: "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)" }}>
        <div ref={trackRef} className="flex gap-6 overflow-x-scroll"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", padding: "8px 48px 20px", cursor: "grab" }}
          onScroll={handleTrackScroll}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}>
          {testimonials.map((t, idx) => (
            <article key={idx} className="flex flex-col flex-shrink-0 w-[350px] md:w-[400px]"
              style={{
                background: "#101017",
                borderRadius: "19.27px",
                border: "1px solid #5A21A8",
                padding: "20px",
                gap: "12px",
                boxShadow: "0 9.64px 33.72px 0 rgba(13,17,23,0.08), 0 1.2px 2.41px 0 rgba(13,17,23,0.04)",
                userSelect: "none",
              }}>
              <div className="flex items-center gap-3">
                <div className="rounded-full overflow-hidden flex-shrink-0" style={{ width: 48, height: 48, backgroundColor: t.badgeColor }}>
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">{t.name}</p>
                  <p className="lowercase" style={{ color: "rgba(255,255,255,0.55)", fontSize: "12px" }}>{t.role}</p>
                </div>
              </div>
              <hr style={{ border: 0, height: "1px", background: "rgba(255,255,255,0.1)", margin: "2px 0" }} />
              <p className="lowercase leading-relaxed" style={{ color: "rgba(255,255,255,0.75)", fontSize: "14px" }}>{t.quote}</p>
            </article>
          ))}
        </div>
      </div>

      {/* Mobile dots — 1 per card, hidden on desktop */}
      <div className="flex md:hidden justify-center items-center gap-2 mt-6">
        {testimonials.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToCard(i)}
            style={{
              width: i === activeDot ? "24px" : "8px",
              height: "8px",
              borderRadius: "9999px",
              backgroundColor: i === activeDot ? "#721AF0" : "rgba(255,255,255,0.25)",
              border: "none",
              cursor: "pointer",
              transition: "all 0.25s ease",
              padding: 0,
              flexShrink: 0,
            }}
          />
        ))}
      </div>

      {/* Desktop dots — 1 per page, hidden on mobile */}
      <div className="hidden md:flex justify-center items-center gap-2 mt-6">
        {Array.from({ length: DESKTOP_DOT_COUNT }, (_, i) => (
          <button
            key={i}
            onClick={() => scrollToPage(i)}
            style={{
              width: i === activeDesktopDot ? "24px" : "8px",
              height: "8px",
              borderRadius: "9999px",
              backgroundColor: i === activeDesktopDot ? "#721AF0" : "rgba(255,255,255,0.25)",
              border: "none",
              cursor: "pointer",
              transition: "all 0.25s ease",
              padding: 0,
              flexShrink: 0,
            }}
          />
        ))}
      </div>

      {/* Stats */}
      <div className="w-full px-1 sm:px-5 md:px-16 lg:px-20 mt-10 md:mt-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
          {stats.map((s, i) => (
            <div key={i} className="flex flex-col items-center gap-1 md:gap-2">
              <span className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(32px, 10vw, 64px)", lineHeight: "100%", letterSpacing: "0px", color: "#721AF0" }}>{s.value}</span>
              <span className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "clamp(12px, 3.5vw, 24px)", lineHeight: "1.4", letterSpacing: "1.4px", color: "#FFFFFF" }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   SECTION 9: FAQ
───────────────────────────────────────── */
function FAQSection() {
  const [open, setOpen] = useState(null);
  return (
    <section className="relative z-10 w-full px-1 sm:px-5 md:px-16 lg:px-20 py-10 md:py-24">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h2 className="lowercase text-center mb-2 md:mb-3" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "clamp(26px, 8vw, 56px)", lineHeight: "1.1", letterSpacing: "-0.8px" }}>
          <span style={{ color: "#FFFFFF" }}>frequently asked </span>
          <span style={{ color: "#721AF0" }}>questions</span>
        </h2>
        <p className="lowercase text-center mb-6 md:mb-12" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "clamp(14px, 4vw, 22px)", lineHeight: "1.4", letterSpacing: "0px", color: "#CCC3DA" }}>
          everything you need to know about getting started with beɘmz.
        </p>

        {/* FAQ items */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => {
            const isOpen = open === i;
            return (
              <div key={i} style={{
                borderRadius: "20px",
                border: `1px solid ${isOpen ? "#721AF0" : "rgba(74,68,87,0.2)"}`,
                backgroundColor: isOpen ? "#0F0C10" : "#1A1A1A",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                padding: "16px",
                gap: isOpen ? "12px" : "0",
                display: "flex",
                flexDirection: "column",
              }}>
                <button className="w-full flex items-center justify-between gap-3 text-left"
                  onClick={() => setOpen(isOpen ? null : i)}>
                  <div className="flex items-center gap-3 flex-1">
                    {/* Circle indicator */}
                    <div className="flex-shrink-0 rounded-full" style={{
                      width: "12px", height: "12px",
                      backgroundColor: isOpen ? "#721AF0" : "transparent",
                      border: `1px solid #721AF0`,
                    }} />
                    <span className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "clamp(13px, 4vw, 20px)", lineHeight: "1.4", letterSpacing: "0px", color: "#FFFFFF" }}>{faq.q}</span>
                  </div>
                  {/* +/- icon */}
                  <span className="flex-shrink-0 text-lg font-light leading-none" style={{ color: "#A166F5" }}>{isOpen ? "−" : "+"}</span>
                </button>
                {isOpen && (
                  <p className="lowercase leading-relaxed pl-[27px]" style={{ color: "rgba(255,255,255,0.6)", fontSize: "clamp(14px, 2vw, 18px)", fontFamily: "Inter, sans-serif" }}>{faq.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   ROOT
───────────────────────────────────────── */
export default function LandingPageNew() {
  return (
    <div className="w-full font-lexend" style={{ backgroundColor: "#020013" }}>
      <Hero />
      <HowItWorksSection />
      <WhyBusinessesChooseSection />
      <CustomersDisappearingSection />
      <FivePlatformsSection />
      <KeyFeaturesSection />
      <PlatformOverviewSection />
      <IndustriesSection />
      <CaseStudiesSection />
      <StatsTestimonialsSection />
      <FAQSection />
      <SharedCTASection />
    </div>
  );
}
